import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, LinearProgress } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { RadioButtonGroup, TextFieldElement } from 'react-hook-form-mui';
import * as yup from 'yup';
import { useAuthContext } from '../../../../../../context/auth';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION_INPUT_FORMAT,
	DATE_PRECISION_PLACEHOLDER,
	FORM_MESSAGE,
} from '../../../../../fastForm/data/constants';
import { DateField } from '../../../../../fastForm/fields';
import { dateConversion } from '../../../../../fastForm/utils/dateConversion';
import { FileUploadField } from '../../../../../hookFormFields';
import TablesAPI, { AddTableRequest } from '../../../api/tables';
import {
	MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
	MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
	TABLES_MODAL_CANCEL_BUTTON,
	TABLES_MODAL_EFFECTIVE_DATE_FIELD,
	TABLES_MODAL_FAILURE_MESSAGE,
	TABLES_MODAL_FILE_FIELD,
	TABLES_MODAL_FILE_TYPE_FIELD,
	TABLES_MODAL_FINISHED_BUTTON,
	TABLES_MODAL_LOADING_MESSAGE,
	TABLES_MODAL_SUBMIT_BUTTON,
	TABLES_MODAL_SUCCESS_MESSAGE,
	TABLES_MODAL_TABLE_NAME_FIELD,
	TABLES_MODAL_UPLOAD_MORE_BUTTON,
} from '../../../data/constants';
import { TABLE_KEY, useFilterParams } from '../Tables';

const DATE_PICKER_PRECISION = 'hour';

interface TableFormData {
	file: FileList;
	tableName: string;
	effectiveDate: string;
	fileType: 'zip' | 'csv';
}

interface AddTablesFormProps {
	tenantHostname?: string;
	handleClose: () => void;
}

const AddTablesForm = (props: AddTablesFormProps) => {
	const authData = useAuthContext();
	const timezone = authData?.auth?.timezone;
	const { asOfDate, tableName } = useFilterParams();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { handleClose, tenantHostname } = props;
	const [successState, setSuccessState] = React.useState(false);

	const {
		mutateAsync: addTable,
		isLoading,
		data,
	} = TablesAPI.useAddTableMutation(
		[TABLE_KEY, tenantId || tenantHostname, asOfDate, tableName],
		authToken,
		tenantHostname,
	);

	const { handleSubmit, control, register, watch, reset } =
		useForm<TableFormData>({
			resolver: yupResolver(
				yup.object().shape({
					effectiveDate: yup
						.string()
						.required(FORM_MESSAGE.required)
						.test(
							'is-valid-date',
							DATE_FIELD_VALIDATIONS.INVALID_FORMAT_ERROR_MESSAGE +
								DATE_PRECISION_PLACEHOLDER[DATE_PICKER_PRECISION],
							(value) => {
								const date = dateConversion.dateFormStringToDayJs(
									value,
									DATE_PICKER_PRECISION,
								);
								return date?.isValid();
							},
						),
					tableName: yup.string().when('fileType', {
						is: 'csv',
						then: yup.string().required(FORM_MESSAGE.required),
					}),
					file: yup.mixed().test({
						test: (value) => {
							if (!value || value.length === 0 || !value[0]?.name) {
								return false;
							} else {
								return true;
							}
						},
						message: FORM_MESSAGE.required,
					}),
				}),
			),
			mode: 'onChange',
			defaultValues: {
				tableName: '',
				file: null,
				effectiveDate: dayjs()
					.hour(0)
					.minute(0)
					.second(0)
					.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]),
				fileType: 'zip',
			},
		});

	const fileType = watch('fileType');

	const onSubmit: SubmitHandler<TableFormData> = async (data) => {
		const request = {} as AddTableRequest;

		request.file = data.file[0];
		if (data.tableName) {
			request.tableName = data.tableName;
		}
		if (data.effectiveDate) {
			request.asOfTimestamp = parseInt(
				dateConversion.convertDateStringToZonedTimestamp(
					data.effectiveDate,
					DATE_PICKER_PRECISION,
					timezone,
				),
			);
		}

		await addTable(request);

		setSuccessState(true);
	};

	const showFormInputs = !isLoading && !successState;

	return (
		<Box
			onSubmit={handleSubmit(onSubmit)}
			component='form'
			sx={{ pt: 1, pb: 2 }}
		>
			<Collapse in={isLoading}>
				<Stack sx={{ width: '100%' }} spacing={2}>
					{TABLES_MODAL_LOADING_MESSAGE()}
					<LinearProgress />
				</Stack>
			</Collapse>
			<Collapse in={successState}>
				<Stack spacing={2}>
					{data?.successfulTables.length > 0 && (
						<Alert
							icon={false}
							security='success'
							sx={{
								alignItems: 'flex-start',
								width: '100%',
								'&.MuiAlert-message': {
									width: '100%',
								},
							}}
						>
							<Accordion
								disableGutters
								sx={{
									backgroundColor: 'transparent',
									border: 'none',
									boxShadow: 'none',
									width: MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls='success-tables-list'
								>
									<Stack spacing={2} direction='row'>
										<CheckCircleIcon color='inherit' />
										<Typography>
											{data?.successfulTables.length +
												' ' +
												TABLES_MODAL_SUCCESS_MESSAGE(
													data?.successfulTables.length > 1,
												)}
										</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<Box
										sx={{
											maxHeight: MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
											overflowX: 'hidden',
										}}
									>
										<Stack spacing={1}>
											{data?.successfulTables.map((success) => (
												<Typography key={success.tableName} variant='caption'>
													{success.tableName}
												</Typography>
											))}
										</Stack>
									</Box>
								</AccordionDetails>
							</Accordion>
						</Alert>
					)}
					{data?.failedTables.length > 0 && (
						<Alert
							icon={false}
							severity='error'
							sx={{
								alignItems: 'flex-start',
								width: '100%',
								'&.MuiAlert-message': {
									width: '100%',
								},
							}}
						>
							<Accordion
								disableGutters
								sx={{
									backgroundColor: 'transparent',
									border: 'none',
									boxShadow: 'none',
									width: MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls='failed-tables-list'
								>
									<Stack spacing={2} direction='row'>
										<ErrorIcon color='inherit' />
										<Typography>
											{data?.failedTables.length +
												' ' +
												TABLES_MODAL_FAILURE_MESSAGE(
													data?.failedTables.length > 1,
												)}
										</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<Box
										sx={{
											maxHeight: MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
											overflowY: 'scroll',
											overflowX: 'hidden',
										}}
									>
										<Stack spacing={1}>
											{data?.failedTables.map((failure) => (
												<Typography key={failure.tableName} variant='caption'>
													{failure.errorDetails}
												</Typography>
											))}
										</Stack>
									</Box>
								</AccordionDetails>
							</Accordion>
						</Alert>
					)}
				</Stack>
			</Collapse>
			<Collapse in={showFormInputs}>
				<Stack spacing={2}>
					<RadioButtonGroup
						control={control}
						row
						label={TABLES_MODAL_FILE_TYPE_FIELD()}
						name='fileType'
						options={[
							{
								id: 'zip',
								label: 'Zip File',
							},
							{
								id: 'csv',
								label: 'CSV File',
							},
						]}
					/>
					<FileUploadField
						sx={{ mt: 2 }}
						label={TABLES_MODAL_FILE_FIELD()}
						name='file'
						accept={fileType === 'csv' ? '.csv' : 'application/zip'}
						disabled={isLoading}
						control={control}
						register={register}
					/>
					<Collapse in={fileType === 'csv'}>
						<TextFieldElement
							sx={{ mt: 1, mb: 1 }}
							name='tableName'
							label={TABLES_MODAL_TABLE_NAME_FIELD()}
							variant='outlined'
							control={control}
							disabled={isLoading}
							fullWidth
						/>
					</Collapse>
					<DateField<TableFormData>
						name='effectiveDate'
						title={TABLES_MODAL_EFFECTIVE_DATE_FIELD()}
						control={control}
						disabled={isLoading}
						defaultToMidnight={true}
						precision={DATE_PICKER_PRECISION}
						authData={authData}
						order={0}
						values={[]}
						disableTimePicker={false}
						showCalendar
						type='date'
						variant='outlined'
					/>
				</Stack>
			</Collapse>
			<Stack
				direction='row'
				spacing={2}
				justifyContent='flex-end'
				sx={{ mt: 2 }}
			>
				{!successState && (
					<Button onClick={handleClose} color='inherit' disabled={isLoading}>
						{TABLES_MODAL_CANCEL_BUTTON()}
					</Button>
				)}
				{!successState && (
					<Button
						type='submit'
						variant='contained'
						color='secondary'
						disabled={isLoading}
					>
						{TABLES_MODAL_SUBMIT_BUTTON()}
					</Button>
				)}
				{successState && (
					<Button onClick={handleClose} color='inherit' disabled={isLoading}>
						{TABLES_MODAL_FINISHED_BUTTON()}
					</Button>
				)}
				{successState && (
					<Button
						type='button'
						variant='contained'
						color='secondary'
						disabled={isLoading}
						onClick={() => {
							setSuccessState(false);
							reset();
						}}
					>
						{TABLES_MODAL_UPLOAD_MORE_BUTTON()}
					</Button>
				)}
			</Stack>
		</Box>
	);
};

export default AddTablesForm;
